import { useState } from 'react';
import AIGenerateModal from './AIGenerateModal';
import UploadModal from './UploadModal';
import CameraModal from './CameraModal';
import AIDesignGenerator from './AIDesignGenerator';

function CreateSection({ user, platforms }) {
  const [activeCreationType, setActiveCreationType] = useState('post');
  const [showAIGenerate, setShowAIGenerate] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [showDesignGenerator, setShowDesignGenerator] = useState(false);

  // Handle design generated
  const handleDesignGenerated = (imageBase64) => {
    // Open the upload modal with the generated design
    setShowDesignGenerator(false);
    // Could pass the image to upload modal or save directly
    console.log('Design generated, can be used in editor');
  };

  return (
    <div className="space-y-6">
      {/* Creation Type Toggle */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          data-testid="create-post-button"
          onClick={() => setActiveCreationType('post')}
          className={`relative p-3 rounded-xl border-2 transition ${ 
            activeCreationType === 'post'
              ? 'accent-gradient border-transparent'
              : ''
          }`}
          style={activeCreationType !== 'post' ? {
            backgroundColor: 'var(--card-bg)',
            borderColor: 'var(--border-color)'
          } : {}}
        >
          <div className="flex items-center justify-center space-x-2">
            <span className="text-xl">📝</span>
            <span className="text-base font-bold" style={{ color: activeCreationType === 'post' ? '#ffffff' : 'var(--text-primary)' }}>Create AI Post</span>
          </div>
        </button>

        <button
          data-testid="create-design-button"
          onClick={() => setShowDesignGenerator(true)}
          className="relative p-3 rounded-xl border-2 transition hover:opacity-90"
          style={{
            backgroundColor: 'var(--card-bg)',
            borderColor: 'var(--accent-from)'
          }}
        >
          <div className="absolute top-2 right-2">
            <span className="px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded-full">
              NEW
            </span>
          </div>
          <div className="flex items-center justify-center space-x-2">
            <span className="text-xl">🎨</span>
            <span className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>AI Design</span>
          </div>
        </button>

        <button
          data-testid="create-video-button"
          disabled
          className="relative p-3 rounded-xl border-2 cursor-not-allowed opacity-60"
          style={{
            backgroundColor: 'var(--card-bg)',
            borderColor: 'var(--border-color)'
          }}
        >
          <div className="absolute top-2 right-2">
            <span className="px-2 py-0.5 bg-amber-500 text-black text-xs font-bold rounded-full">
              Coming Soon
            </span>
          </div>
          <div className="flex items-center justify-center space-x-2">
            <span className="text-xl">🎬</span>
            <span className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>Create AI Video</span>
          </div>
        </button>
      </div>

      {/* Create AI Post Section */}
      {activeCreationType === 'post' && (
        <div className="theme-card border rounded-xl p-6 fade-in">
          <h2 className="text-xl font-bold mb-1 accent-text">Create AI Post</h2>
          <p className="mb-6 text-sm" style={{ color: 'var(--text-muted)' }}>Upload multiple images/videos and let AI generate engaging captions</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Upload from Device */}
            <button
              data-testid="upload-device-button"
              onClick={() => setShowUpload(true)}
              className="group relative p-5 rounded-lg border-2 border-dashed transition"
              style={{
                borderColor: 'var(--accent-from)',
                backgroundColor: 'var(--card-bg)',
                opacity: '0.7'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                e.currentTarget.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--card-bg)';
                e.currentTarget.style.opacity = '0.7';
              }}
            >
              <div className="flex flex-col items-center text-center space-y-3">
                <div className="w-14 h-14 accent-gradient rounded-full flex items-center justify-center group-hover:scale-110 transition">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Upload from Device</h3>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Select images or videos</p>
                </div>
              </div>
            </button>

            {/* Take Photo */}
            <button
              data-testid="take-photo-button"
              onClick={() => setShowCamera(true)}
              className="group relative p-5 rounded-lg border-2 border-dashed transition"
              style={{
                borderColor: 'var(--accent-from)',
                backgroundColor: 'var(--card-bg)',
                opacity: '0.7'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--hover-bg)';
                e.currentTarget.style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--card-bg)';
                e.currentTarget.style.opacity = '0.7';
              }}
            >
              <div className="flex flex-col items-center text-center space-y-3">
                <div className="w-14 h-14 accent-gradient rounded-full flex items-center justify-center group-hover:scale-110 transition">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Take Photo</h3>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Use device camera</p>
                </div>
              </div>
            </button>

            {/* AI Generate */}
            <button
              data-testid="ai-generate-button"
              onClick={() => setShowAIGenerate(true)}
              className="group relative p-5 rounded-lg border-2 border-dashed border-teal-500/50 bg-gray-800/50 hover:bg-gray-800 hover:border-teal-500 transition"
            >
              <div className="flex flex-col items-center text-center space-y-3">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-600 to-cyan-600 rounded-full flex items-center justify-center group-hover:scale-110 transition glow-effect">
                  <span className="text-3xl">✨</span>
                </div>
                <div>
                  <h3 className="text-base font-bold text-white mb-1">AI Generate</h3>
                  <p className="text-xs text-gray-400">Create from text</p>
                </div>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {showAIGenerate && (
        <AIGenerateModal
          user={user}
          platforms={platforms}
          onClose={() => setShowAIGenerate(false)}
        />
      )}

      {showUpload && (
        <UploadModal
          user={user}
          platforms={platforms}
          onClose={() => setShowUpload(false)}
        />
      )}

      {showCamera && (
        <CameraModal
          user={user}
          platforms={platforms}
          onClose={() => setShowCamera(false)}
        />
      )}

      {showDesignGenerator && (
        <AIDesignGenerator
          userId={user?.id}
          industry={user?.current_industry || user?.industry || 'Technology'}
          onDesignGenerated={handleDesignGenerated}
          onClose={() => setShowDesignGenerator(false)}
        />
      )}
    </div>
  );
}

export default CreateSection;
